import { useCallback } from 'react';
import { ApolloError } from '@apollo/client';
import {
  StringGroupCreateMetadataInput,
  SubmissionType,
  useSubmitStringGroupTranslationMutation,
} from 'src/generated/graphql';
import { normalizeLocaleIdentifier } from 'src/common/utils/locale';
import {
  getAppSyncErrorInfo,
  getAppSyncErrorType,
} from 'src/api/graphqlErrorType';
import {
  DeniedNamespace,
  getAccessDeniedMessage,
  getAccessDeniedNamespace,
  isAccessDenied,
} from 'src/common/authz/isAccessDenied';
import { DraftState } from '../types';
import { TranslateFn, useTranslation } from 'src/i18n/shim/useTranslation';

export interface SubmitResult {
  success: boolean;
  version?: number;
  error?: string;
  /**
   * The namespace a refusal named, which the page turns into a Bindle link beside
   * `error`. Kept out of `error` because that string is rendered verbatim. Absent
   * for every non-refusal and for a refusal the service sent no detail with.
   */
  deniedNamespace?: DeniedNamespace;
  /**
   * Present only when the service refused the submission on change-control
   * grounds. The page shows this in a dialog rather than the Flashbar: it is the
   * one refusal the operator must act on before retrying (cite a different MCM,
   * or wait), so it should not scroll away with the other notifications.
   */
  changeControlBlocked?: ChangeControlRefusal;
}

/**
 * A change-control refusal as the service reported it. `code` is the backend's
 * `ChangeControlBlockReason` (`OVERRIDE_REQUIRED`, `INVALID_OVERRIDE`,
 * `UNVERIFIABLE`), carried in AppSync `errorInfo` by the composite-schema
 * resolver; `'UNKNOWN'` when a transport dropped it. Not displayed — it
 * classifies the refusal for tests and future telemetry. `message` is the
 * service's own sentence naming the failed check, rendered verbatim.
 */
export interface ChangeControlRefusal {
  code: string;
  message: string;
}

interface UseSubmitTranslationResult {
  /**
   * @param notificationEmail recipient for the completion notification
   * @param mcmId optional change-control override — the approved MCM the operator
   *   cites when submitting during a Restricted/Blocked window. Absent or blank
   *   means "no override cited" and the variable is omitted from the mutation.
   */
  submit: (notificationEmail: string, mcmId?: string) => Promise<SubmitResult>;
  loading: boolean;
}

/**
 * AppSync `errorType` of a submission refused on change-control grounds (a cited
 * MCM failed validation, or could not be verified). Its message is the backend's
 * own sentence naming the failed check and is rendered verbatim.
 */
export const CHANGE_CONTROL_BLOCKED_ERROR_TYPE =
  'ChangeControlBlockedException';

/**
 * Classifies a submit failure as a change-control refusal, or `undefined` for
 * anything else. Keyed on the AppSync `errorType` — the same signal
 * `extractSubmitError` uses for the typed fallback — so the two never disagree
 * about what kind of failure this was. The `code` rides in `errorInfo` (the
 * composite-schema resolver puts it there so the message stays the service's
 * prose); a missing or non-string code degrades to `'UNKNOWN'` rather than
 * demoting the refusal to a generic error.
 *
 * Exported so the rollback path (`submitTranslationUtils`) classifies a refusal
 * with this same function: a rollback is submitted through the same mutation and
 * refused by the same gate, so the two must never disagree about what counts as
 * a change-control refusal.
 */
export function toChangeControlRefusal(
  e: unknown,
  message: string,
): ChangeControlRefusal | undefined {
  if (!(e instanceof ApolloError) || e.graphQLErrors.length === 0) {
    return undefined;
  }
  const gqlError = e.graphQLErrors[0];
  if (getAppSyncErrorType(gqlError) !== CHANGE_CONTROL_BLOCKED_ERROR_TYPE) {
    return undefined;
  }
  const code = getAppSyncErrorInfo(gqlError)?.['code'];
  return { code: typeof code === 'string' ? code : 'UNKNOWN', message };
}

/**
 * Shapes the cited MCM for the mutation variable. A blank id collapses to
 * `undefined` so the variable is ABSENT rather than an empty string: the resolver
 * forwards any present value and the backend refuses a blank with a 400, so an
 * operator who typed nothing must submit as "no override cited", not as "an
 * invalid override". A non-blank id is trimmed and otherwise never reshaped —
 * validity is the backend's call.
 */
export const toMcmIdVariable = (mcmId?: string): string | undefined => {
  const trimmed = mcmId?.trim();
  return trimmed ? trimmed : undefined;
};

/**
 * The create-modal values that ride on a new group's FIRST submission — the only
 * moment the backend accepts them. Structurally a subset of
 * `NewStringGroupMetadata` (the router-state handoff from the Create String Group
 * modal), so the page can pass that object straight in.
 */
export interface CreateGroupMetadataSource {
  description?: string;
  teamName?: string;
}

/**
 * Builds the `groupMetadata` mutation argument from the create-modal values,
 * or `undefined` when there is nothing valid to send.
 *
 * ALL-OR-NOTHING, matching the contract: both members are non-null in the
 * GraphQL input type and `@required` with `@length(min: 1)` in Smithy, so a
 * half-populated shape is not a weaker submission — it is a rejected one. If
 * either value is missing or blank after trimming, the whole argument collapses
 * to `undefined` and the field is left out of the request entirely. That is
 * strictly better than sending a blank string: the resolver keys off presence,
 * and the backend answers 400 both for a blank member and for the mere presence
 * of the field on a group that already has versions.
 *
 * The Create String Group form requires a non-blank description AND team name
 * (see `createStringGroupValidation`), so a submission that came through the
 * modal always yields both. The collapse path covers a detail page opened
 * directly, with no create-modal handoff at all.
 */
function buildGroupMetadata(
  source: CreateGroupMetadataSource | null | undefined,
): StringGroupCreateMetadataInput | undefined {
  const description = source?.description?.trim();
  const teamName = source?.teamName?.trim();
  if (!description || !teamName) {
    return undefined;
  }
  return { description, teamName };
}

/**
 * Submits a string group for translation via the SubmitStringGroupTranslation
 * GraphQL mutation. Converts the local DraftState into the mutation variables
 * (added/edited/deleted arrays + baseVersion + cxIds).
 *
 * CX ID resolution: if the user edited CX IDs in the draft (`groupEdits.cxIds`),
 * those are sent; otherwise the current server CX IDs are forwarded unchanged so
 * that submissions never accidentally clear the group's CX ID selection.
 *
 * `sourceLocale` and `cxFamilyId` are FROZEN group properties, not user choices:
 * they are read straight off the group (see `useStringGroupData`, which supplies
 * an `en-US` fallback for the locale) and forwarded unchanged. The group's first
 * version defines them and the backend rejects a later submission that disagrees
 * with a 400, so they are deliberately never editable in the UI.
 *
 * @param namespaceId
 * @param stringGroupId
 * @param draftState
 * @param serverCxIds
 * @param serverSourceLocale the group's source locale; normalized to the BCP-47
 *   hyphen form at the submit boundary because the backend's `LocaleIdentifier`
 *   validation rejects the legacy underscore form (`en_US`).
 * @param cxFamilyId the group's CX family — distinct from `cxIds`, which selects
 *   the domains within that family.
 * @param isNewGroup true when the page is still rendering create-modal prefill
 *   metadata, i.e. the group has NO persisted version yet (see
 *   `useStringGroupData`'s `isPrefill`). Such a group's prefill `version: 1` is
 *   a local placeholder, so `baseVersion` is omitted from the mutation entirely
 *   and the backend numbers the first version `1` rather than deriving
 *   `1 + 1 = 2`. It must be driven off that genuinely-new signal and nothing
 *   looser: the backend rejects an absent `baseVersion` with a 409 once the
 *   group has any version.
 * @param createMetadata the Create String Group modal's `teamName`/`description`
 *   (the router-state prefill handed to `useStringGroupData`). Sent as
 *   `groupMetadata` ONLY when `isNewGroup` — the backend applies it to the
 *   group's first version and rejects its PRESENCE on a group that already has
 *   versions with a 400, so it is gated on the very same flag that omits
 *   `baseVersion`. The two conditions are deliberately the same expression here
 *   rather than two independent props, because if they ever diverged a routine
 *   submit would start failing with a 400. Ignored for an existing group.
 */
export function useSubmitTranslation(
  namespaceId: string,
  stringGroupId: string,
  draftState: DraftState | null,
  serverCxIds: string[],
  serverSourceLocale: string,
  cxFamilyId: string,
  isNewGroup = false,
  createMetadata?: CreateGroupMetadataSource | null,
): UseSubmitTranslationResult {
  const [mutate, { loading }] = useSubmitStringGroupTranslationMutation();
  const { t } = useTranslation();

  const submit = useCallback(
    async (
      notificationEmail: string,
      mcmId?: string,
    ): Promise<SubmitResult> => {
      if (!draftState) {
        return { success: false, error: t('sgd-submit-error-no-changes') };
      }

      // Resolve effective CX IDs: draft-edited values take precedence over
      // the current server state; unchanged submissions pass through the
      // server's current selection.
      const effectiveCxIds = draftState.groupEdits?.cxIds ?? serverCxIds;

      try {
        const { data } = await mutate({
          variables: {
            namespaceId,
            stringGroupId,
            // The CURRENT server version, not the version this submission will
            // create: the backend derives nextVersion = baseVersion + 1 itself and
            // uses this value purely for optimistic-concurrency detection (a stale
            // value yields a 409). `draftState.baseVersion` is stamped from the
            // group's server version when the first edit is made
            // (see createEmptyDraft / useDraftState).
            //
            // OMITTED for a genuinely new group (create-modal prefill, nothing
            // persisted server-side): its `version: 1` is a local placeholder, so
            // sending `baseVersion: '1'` would have the backend number the group's
            // FIRST version 2. `undefined` leaves the GraphQL variable absent,
            // which the backend accepts only while the group has no predecessor —
            // hence the strict `isNewGroup` signal rather than any looser
            // condition (an absent baseVersion on an existing group is a 409).
            baseVersion: isNewGroup
              ? undefined
              : draftState.baseVersion.toString(),
            added:
              draftState.added.length > 0
                ? draftState.added.map((s) => ({
                    stringId: s.stringId,
                    sourceText: s.sourceText,
                    contextNote: s.contextNote,
                    inlineMarkupType: s.inlineMarkupType,
                    messageFormatType: s.messageFormatType,
                    variants: s.variants?.map((v) => ({
                      cxId: v.cxId,
                      sourceText: v.sourceText,
                      contextNote: v.contextNote,
                    })),
                  }))
                : undefined,
            edited:
              draftState.edited.length > 0
                ? draftState.edited.map((s) => ({
                    stringId: s.stringId,
                    sourceText: s.sourceText,
                    contextNote: s.contextNote,
                    inlineMarkupType: s.inlineMarkupType,
                    messageFormatType: s.messageFormatType,
                    variants: s.variants?.map((v) => ({
                      cxId: v.cxId,
                      sourceText: v.sourceText,
                      contextNote: v.contextNote,
                    })),
                  }))
                : undefined,
            deleted:
              draftState.deleted.length > 0 ? draftState.deleted : undefined,
            cxIds: effectiveCxIds,
            // Normalize to the BCP-47 hyphen form the backend's LocaleIdentifier
            // validation accepts (see normalizeLocaleIdentifier). This is the single
            // submit boundary the group locale passes through — it matters more now
            // that the field is mandatory, since an unnormalized `en_US` fails the
            // whole submission rather than one string.
            sourceLocale: normalizeLocaleIdentifier(serverSourceLocale),
            cxFamilyId,
            notificationEmail: notificationEmail || undefined,
            // Distinguishes a routine content submit from a rollback (see RollbackModal),
            // so the backend can tell the two apart.
            submissionType: SubmissionType.Submit,
            // Create-only metadata from the Create String Group modal. Gated on the
            // SAME `isNewGroup` expression as `baseVersion` above — the backend
            // accepts it only on the submission that creates the group and answers
            // 400 ValidationException if it is present once versions exist. Left
            // `undefined` (variable absent) otherwise, and `buildGroupMetadata`
            // also returns `undefined` unless BOTH values are present and non-blank,
            // so a partial or empty object never reaches the resolver.
            groupMetadata: isNewGroup
              ? buildGroupMetadata(createMetadata)
              : undefined,
            // Change-control override (MCM). Absent from the variables entirely when
            // nothing was cited — see toMcmIdVariable.
            mcmId: toMcmIdVariable(mcmId),
          },
        });

        const newVersion = data?.submitStringGroupTranslation.version;
        return { success: true, version: newVersion };
      } catch (e) {
        const error = extractSubmitError(t, e);
        return {
          success: false,
          error,
          // Read off the same matched error `extractSubmitError` takes the refusal
          // message from, so the link and the sentence beside it always describe
          // one refusal. Absent for a fault, which has no Bindle to offer.
          deniedNamespace: getAccessDeniedNamespace(e),
          // Same sentence as `error`, so the dialog and the field never diverge.
          changeControlBlocked: toChangeControlRefusal(e, error),
        };
      }
    },
    [
      draftState,
      mutate,
      namespaceId,
      stringGroupId,
      serverCxIds,
      serverSourceLocale,
      cxFamilyId,
      isNewGroup,
      createMetadata,
      t,
    ],
  );

  return { submit, loading };
}

/**
 * Extracts a user-friendly error message from a mutation failure. Exported so the
 * rollback path (`submitTranslationUtils`) reads a change-control refusal through
 * the same function — same matched-error rule, same blank-refusal fallback.
 *
 * Only the fallbacks it owns are localized: a message the service sent is upstream content and is
 * returned verbatim.
 */
export function extractSubmitError(t: TranslateFn, e: unknown): string {
  const genericFallback = t('sgd-submit-error-generic');

  // Non-Error throwables can't originate from the Apollo mutation (it always
  // rejects with an Error/ApolloError); this guard is purely defensive.
  // istanbul ignore next -- unreachable via the mutation path
  if (!(e instanceof Error)) {
    return genericFallback;
  }

  // Classified FIRST and across the WHOLE error list, through the shared
  // `src/common/authz` helpers, so a submit cannot be reported as a generic
  // failure while the rest of the page calls the same response a refusal.
  //
  // The matched error's message is surfaced verbatim, bypassing the
  // unhelpful-message fallback the typed branches below use: the service decides
  // how much to reveal about a resource the caller may not see. It reads the
  // MATCHED error, not Apollo's concatenated aggregate, so a co-occurring
  // unrelated sentence never rides along, and answers '' only for a refusal the
  // service left blank — hence the generic fallback.
  if (isAccessDenied(e)) {
    return getAccessDeniedMessage(e) || genericFallback;
  }

  // ApolloError carries the structured GraphQL errors AppSync returned.
  if (e instanceof ApolloError && e.graphQLErrors.length > 0) {
    const gqlError = e.graphQLErrors[0];
    // AppSync puts `errorType` at the TOP LEVEL of the error, which is what
    // getAppSyncErrorType reads; `extensions.errorType` does not match real
    // traffic.
    const errorType = getAppSyncErrorType(gqlError);
    const msg = gqlError.message;

    // If the message is unhelpful (empty JSON, blank, etc.), provide a typed fallback
    if (!msg || !msg.trim() || /^\s*[{\[]/.test(msg.trim())) {
      switch (errorType) {
        case 'ConflictException':
          return t('sgd-submit-error-conflict');
        case 'ValidationException':
          return t('sgd-submit-error-validation');
        case CHANGE_CONTROL_BLOCKED_ERROR_TYPE:
          // Normally unreachable: the backend always names the failed check. Kept so a
          // blank refusal still reads as change control rather than a generic failure.
          return t('sgd-submit-error-change-control');
        default:
          return genericFallback;
      }
    }

    return msg;
  }

  // Fallback for non-Apollo errors (network errors, etc.)
  const raw = e.message;
  if (!raw || !raw.trim() || /^\s*[{\[]/.test(raw.trim())) {
    return genericFallback;
  }
  return raw;
}
